import type { Channel, PublicUser } from '@orbit-sync/shared-types';

/**
 * Display helpers.
 *
 * Names are resolved through `chat/directoryStore.ts`, which batches
 * `GET /api/v1/users/lookup` calls for whatever ids are on screen. A component
 * calls `useDirectory(ids)` and passes the result here.
 *
 * The id fragment is still the fallback, for the moment before a lookup lands
 * and for an account that no longer exists. It is honest about being a stand-in
 * rather than guessing at a name.
 */

/** A person's name, or a short honest stand-in until the lookup resolves. */
export function memberLabel(
  userId: string,
  currentUserId: string | null,
  directory: Record<string, PublicUser> = {},
): string {
  if (currentUserId && userId === currentUserId) {
    return 'You';
  }

  return directory[userId]?.displayName ?? `Member ${userId.slice(0, 6)}`;
}

/** The other participant in a direct channel, or `null` if it is not a 1:1. */
export function counterpartId(channel: Channel, currentUserId: string | null): string | null {
  if (channel.type !== 'direct') {
    return null;
  }

  return channel.memberIds.find((id) => id !== currentUserId) ?? null;
}

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });
const DATE_FORMAT = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

export function formatTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : TIME_FORMAT.format(date);
}

/** Day heading for a transcript separator. */
export function formatDay(iso: string): string {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();

  return isToday ? 'Today' : DATE_FORMAT.format(date);
}

export function sameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

const WEEKDAY_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const SHORT_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'numeric',
  year: '2-digit',
});

/**
 * Compact timestamp for a conversation row.
 *
 * Widens as the message gets older - a time today, a weekday this week, a date
 * beyond that - so the column stays narrow while still being unambiguous.
 */
export function formatListTime(iso: string): string {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();

  if (date.toDateString() === now.toDateString()) {
    return TIME_FORMAT.format(date);
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  // Inside a week, the weekday is more readable than a date.
  if (now.getTime() - date.getTime() < 6 * 24 * 60 * 60 * 1000) {
    return WEEKDAY_FORMAT.format(date);
  }

  return SHORT_DATE_FORMAT.format(date);
}

/** Collapses a message to a single sidebar line. */
export function previewText(content: string): string {
  return content.replace(/\s+/g, ' ').trim();
}

/** "2.1 MB", "512 KB", "12 B" - decimal units, as file managers show them. */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1000;
  let unit = 0;

  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }

  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/**
 * "typing…" for a direct chat; "Ada is typing…", "Ada and Grace are typing…" or
 * "3 people are typing…" for a group. `null` when nobody is.
 */
export function typingLabel(
  typing: Record<string, number>,
  currentUserId: string | null,
  directory: Record<string, PublicUser>,
  isDirect: boolean,
): string | null {
  const ids = Object.keys(typing).filter((id) => id !== currentUserId);

  if (ids.length === 0) {
    return null;
  }

  if (isDirect) {
    return 'typing…';
  }

  const names = ids.map((id) => memberLabel(id, currentUserId, directory));

  if (names.length === 1) {
    return `${names[0]} is typing…`;
  }

  return names.length === 2
    ? `${names[0]} and ${names[1]} are typing…`
    : `${names.length} people are typing…`;
}
